import React, { useEffect, useRef } from "react";
import MaterialTable from "material-table";
import GetAppIcon from "@material-ui/icons/GetApp";
import ImportLots from "components/Imports/ImportLots";
import LayersIcon from "@material-ui/icons/Layers";
import useImport from "hooks/useImport";
import useExport from "hooks/useExport";
import PropTypes from "prop-types";
import { materialTableLocalization } from "../../utils/materialTableLocalization";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import LockIcon from "@material-ui/icons/Lock";

export default function TableLot({
  style,
  className,
  idBlock,
  blocks,
  handleClickSave,
  handleDeleteLot,
  lot,
  getLots
}) {
  const cellCenter = { textAlign: "center" };
  const col = (width) => ({ textAlign: "center", width, maxWidth: width });

  const baseColumns = [
    { title: "#", field: "NUMERO", type: "numeric", editable: "never", cellStyle: col(50), headerStyle: col(50) },
    {
      title: "Estatus", field: "DESCRIPCION", type: "string", editable: "never",
      cellStyle: col(100), headerStyle: col(100)
    },
    { title: "Sup. (m²)", field: "SUPERFICIE", type: "numeric", cellStyle: col(80), headerStyle: col(80) },
    {
      title: "Tipo", field: "TIPO", cellStyle: col(90), headerStyle: col(90),
      render: rowData => rowData.TIPO || "—",
      editComponent: props => (
        <Select value={props.value || ""} onChange={e => props.onChange(e.target.value)} fullWidth>
          <MenuItem value="Esquina">Esquina</MenuItem>
          <MenuItem value="Otros">Otros</MenuItem>
        </Select>
      )
    },
    {
      title: "Costo ($)", field: "COSTO", type: "numeric", cellStyle: col(110), headerStyle: col(110),
      render: rowData => "$" + parseFloat(rowData.COSTO || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })
    },
    { title: "Col. Norte", field: "COLINDANCIA_UNO", type: "string", cellStyle: cellCenter, headerStyle: cellCenter },
    { title: "Col. Sur", field: "COLINDANCIA_DOS", type: "string", cellStyle: cellCenter, headerStyle: cellCenter },
    { title: "Col. Este", field: "COLINDANCIA_TRES", type: "string", cellStyle: cellCenter, headerStyle: cellCenter },
    { title: "Col. Oeste", field: "COLINDANCIA_CUATRO", type: "string", cellStyle: cellCenter, headerStyle: cellCenter }
  ];

  const { importModalOpen, handleImportClick, handleImportClose } = useImport();
  const { generateExcelFile } = useExport();
  const tableRef = useRef(null);

  useEffect(() => {
    idBlock && getLots(idBlock);
  }, [idBlock]); // eslint-disable-line react-hooks/exhaustive-deps

  const disponibles = (lot || []).filter(l => !l.ESTATUS || l.ESTATUS === 'D');
  const asignados = (lot || []).filter(l => l.ESTATUS && l.ESTATUS !== 'D');

  return (
    <div style={style} className={className}>
      {/* TABLA EDITABLE — Lotes disponibles */}
      <MaterialTable
        tableReference={tableRef}
        title="Lista de lotes"
        columns={baseColumns}
        data={disponibles}
        localization={materialTableLocalization}
        editable={{
          onRowAdd: newData =>
            new Promise(resolve => {
              handleClickSave(newData, "add", idBlock);
              resolve();
            }),
          onRowUpdate: newData =>
            new Promise(resolve => {
              handleClickSave(newData, "update", idBlock);
              resolve();
            }),
          onRowDelete: oldData =>
            new Promise(resolve => {
              if (handleDeleteLot) {
                handleDeleteLot(oldData, idBlock).then(() => resolve());
              } else {
                resolve();
              }
            })
        }}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50]
        }}
        actions={[
          {
            icon: LayersIcon,
            tooltip: "Importar desde Excel",
            isFreeAction: true,
            onClick: event => {
              event.preventDefault();
              handleImportClick(idBlock);
            }
          },
          {
            icon: GetAppIcon,
            tooltip: "Descargar Layout",
            isFreeAction: true,
            onClick: event => {
              event.preventDefault();
              generateExcelFile();
            }
          }
        ]}
      />

      {/* TABLA SOLO LECTURA — Lotes con contrato */}
      {asignados.length > 0 && (
        <>
          <Divider style={{ margin: "24px 0 12px" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <LockIcon style={{ fontSize: 16, color: "#e65100" }} />
            <Typography style={{ fontSize: 13, fontWeight: 600, color: "#e65100" }}>
              Lotes con contrato asignado ({asignados.length})
            </Typography>
            <Chip label="Solo lectura" size="small"
              style={{ backgroundColor: "#fff3e0", color: "#e65100", fontSize: 10 }} />
          </div>
          <MaterialTable
            title=""
            columns={baseColumns}
            data={asignados}
            localization={materialTableLocalization}
            options={{
              search: false,
              paging: false,
              toolbar: false,
              headerStyle: { fontWeight: 700, backgroundColor: "#fff3e0", fontSize: 12 },
              rowStyle: { backgroundColor: "#fffde7" }
            }}
          />
        </>
      )}

      <ImportLots
        onImport={handleImportClick}
        importModalOpen={importModalOpen}
        handleImportClose={handleImportClose}
        blocks={blocks}
        idBlock={idBlock}
      />
    </div>
  );
}

TableLot.propTypes = {
  manzana: PropTypes.number,
  style: PropTypes.object,
  className: PropTypes.string,
  idBlock: PropTypes.number,
  blocks: PropTypes.array,
  handleClickSave: PropTypes.func,
  lot: PropTypes.array,
  getLots: PropTypes.func
};
